import { useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { cx } from '@platform/core/lib/cx';
import type { IUserRef } from '@platform/core/types';
import { useMembersQuery } from '@platform/state/api';
import { Avatar } from '@platform/ui';
import type { ICommentComposerProps } from './ICommentComposerProps';
import { insertMention, mentionAt, mentionIds } from './mentions';

/**
 * Поле нового коментаря під стрічкою (прототип v4): «Напишіть коментар… (Ctrl+Enter — надіслати)», Ctrl/⌘+Enter надсилає, кнопка
 * «Коментар» праворуч (як у прототипі, доступна й з порожнім полем — тоді нічого не робить). «@» відкриває список учасників (↑ ↓, Enter або Tab — вставити «@Ім'я», Esc — закрити); згадані отримують сповіщення.
 */
export function CommentComposer({ ws, sending, onSend }: ICommentComposerProps) {
  const { t } = useTranslation();
  const { data: members = [] } = useMembersQuery(ws, { skip: !ws });
  const [text, setText] = useState('');
  const [picked, setPicked] = useState<IUserRef[]>([]);
  const [mention, setMention] = useState<{ start: number; query: string } | null>(null);
  const [hi, setHi] = useState(0);
  const area = useRef<HTMLTextAreaElement>(null);
  const q = mention?.query.toLocaleLowerCase() ?? '';
  const options = mention ? members.map((m) => m.user).filter((u) => u.name.toLocaleLowerCase().includes(q)).slice(0, 6) : [];
  const update = (value: string, caret: number) => { setText(value); setMention(mentionAt(value, caret)); setHi(0); };
  const choose = (user: IUserRef) => {
    if (!mention) return;
    const next = insertMention(text, mention.start, area.current?.selectionStart ?? text.length, user.name);
    setText(next.text); setPicked((p) => [...p, user]); setMention(null);
    requestAnimationFrame(() => { area.current?.focus(); area.current?.setSelectionRange(next.caret, next.caret); });
  };
  const send = () => {
    if (!text.trim() || sending) return;
    void onSend(text, mentionIds(text, picked)).then((ok) => { if (ok) { setText(''); setPicked([]); } });
  };
  const onKeyDown = (e: KeyboardEvent) => {
    if (options.length) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); setHi((h) => (h + (e.key === 'ArrowDown' ? 1 : options.length - 1)) % options.length); return; }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); choose(options[hi] ?? options[0]!); return; }
      if (e.key === 'Escape') { e.preventDefault(); setMention(null); return; }
    }
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') { e.preventDefault(); send(); }
  };
  return (
    <div className="rtab-composer">
      <textarea ref={area} className="rtab-composer__text" rows={3} data-comment-composer value={text} placeholder={t('recordPage.comments.placeholder')}
        aria-label={t('recordPage.comments.label')} onChange={(e) => update(e.target.value, e.target.selectionStart)} onKeyDown={onKeyDown}
        onBlur={() => setTimeout(() => setMention(null), 150)} />
      {options.length > 0 && (
        <div className="rtab-composer__mentions" role="listbox" aria-label={t('recordPage.comments.mention')}>
          {options.map((u, i) => (
            <button key={u.id} type="button" role="option" aria-selected={i === hi} className={cx('rtab-composer__opt', i === hi && 'is-hi')}
              onMouseDown={(e) => { e.preventDefault(); choose(u); }} onMouseEnter={() => setHi(i)}>
              <Avatar user={u} size={22} /><span>{u.name}</span>
            </button>
          ))}
        </div>
      )}
      <button type="button" className="rtab-composer__send" disabled={sending} onClick={send}>{t('recordPage.comments.send')}</button>
    </div>
  );
}
